import { useMemo, useState } from 'react';
import Select, { type MultiValue } from 'react-select';

import { useIntl } from '../../hooks';
import { getStatusTranslationKey } from '../../services/detection-status.js';
import { DetectionStatus } from '../../types';
import type { Device } from '../../types/device.js';
import { Button, Card, CardContent, CardHeader, CardTitle, Label } from '../ui';
import { STATUS_COLORS } from './DetectionsSequencesSunburst.js';

const STATUS_BG_COLORS: Record<DetectionStatus, string> = {
  [DetectionStatus.DETECTED]: 'bg-accent-light border-accent-dark',
  [DetectionStatus.SUPPRESSION_SUCCESS]: 'bg-success-light border-success',
  [DetectionStatus.SUPPRESSION_FAILED]: 'bg-destructive-light border-destructive',
  [DetectionStatus.SUPPRESSION_INITIATED]: 'bg-yellow-light border-yellow-dark',
  [DetectionStatus.SUPPRESSION_IN_PROGRESS]: 'bg-orange-light border-orange-dark',
  [DetectionStatus.WHITELISTED]: 'bg-purple-light border-purple-dark',
  [DetectionStatus.LOST]: 'bg-gray-light border-gray-dark',
};

const STATUS_TEXT_COLORS: Record<DetectionStatus, string> = {
  [DetectionStatus.DETECTED]: 'text-accent-dark',
  [DetectionStatus.SUPPRESSION_SUCCESS]: 'text-success-dark',
  [DetectionStatus.SUPPRESSION_FAILED]: 'text-destructive-dark',
  [DetectionStatus.SUPPRESSION_INITIATED]: 'text-yellow-dark',
  [DetectionStatus.SUPPRESSION_IN_PROGRESS]: 'text-orange-dark',
  [DetectionStatus.WHITELISTED]: 'text-purple-dark',
  [DetectionStatus.LOST]: 'text-foreground',
};

const ALL_STATUSES = Object.values(DetectionStatus);

interface DeviceOption {
  value: string;
  label: string;
}

export interface AnalyticsFilters {
  deviceIds: string[];
  from?: string;
  to?: string;
  statuses: DetectionStatus[];
}

interface AnalyticsFiltersPanelProps {
  devices: Device[];
  selectedDeviceIds: string[];
  onDeviceIdsChange: (deviceIds: string[]) => void;
  appliedFilters: { from?: string; to?: string };
  onApplyDateFilters: (filters: { from?: string; to?: string }) => void;
  selectedStatuses: Set<DetectionStatus>;
  onStatusesChange: (statuses: Set<DetectionStatus>) => void;
  statusCounts: Record<DetectionStatus, number>;
  initialFrom?: string;
}

export function AnalyticsFiltersPanel({
  devices,
  selectedDeviceIds,
  onDeviceIdsChange,
  appliedFilters,
  onApplyDateFilters,
  selectedStatuses,
  onStatusesChange,
  statusCounts,
  initialFrom = '',
}: AnalyticsFiltersPanelProps) {
  const intl = useIntl();

  const [from, setFrom] = useState(initialFrom);
  const [to, setTo] = useState('');

  const deviceOptions = useMemo<DeviceOption[]>(
    () =>
      devices.map((d) => ({
        value: d.id,
        label: d.serial,
      })),
    [devices],
  );

  const selectedDeviceOptions = useMemo(
    () => deviceOptions.filter((opt) => selectedDeviceIds.includes(opt.value)),
    [deviceOptions, selectedDeviceIds],
  );

  const totalFromCounts = useMemo(() => {
    return Object.values(statusCounts).reduce((sum, count) => sum + count, 0);
  }, [statusCounts]);

  const handleDeviceChange = (selected: MultiValue<DeviceOption>) => {
    onDeviceIdsChange(selected.map((opt) => opt.value));
  };

  const toggleStatus = (status: DetectionStatus) => {
    const next = new Set(selectedStatuses);
    if (next.has(status)) {
      next.delete(status);
    } else {
      next.add(status);
    }
    onStatusesChange(next);
  };

  const handleApplyFilters = () => {
    onApplyDateFilters({
      from: from || undefined,
      to: to || undefined,
    });
  };

  const handleClearFilters = () => {
    setFrom('');
    setTo('');
    onApplyDateFilters({});
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{intl.formatMessage({ id: 'analytics.filters' })}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Device Selection */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">
            {intl.formatMessage({ id: 'detections.devices' })}
            <span className="ml-2 text-xs font-normal text-foreground">
              ({selectedDeviceIds.length}/{devices.length}{' '}
              {intl.formatMessage({ id: 'analytics.selected' })})
            </span>
          </Label>

          {devices.length === 0 ? (
            <div className="text-center py-4 text-foreground">
              {intl.formatMessage({ id: 'detections.noDevicesAvailable' })}
            </div>
          ) : (
            <Select<DeviceOption, true>
              inputId="device-filter"
              instanceId="device-filter"
              isMulti
              options={deviceOptions}
              value={selectedDeviceOptions}
              onChange={handleDeviceChange}
              placeholder={intl.formatMessage({ id: 'analytics.selectDevices' })}
              noOptionsMessage={() => intl.formatMessage({ id: 'detections.noDevicesAvailable' })}
              formatOptionLabel={(option) => (
                <div className="flex items-center justify-between">
                  <span>{option.label}</span>
                </div>
              )}
              styles={{
                control: (base, state) => ({
                  ...base,
                  minHeight: '40px',
                  borderColor: 'var(--color-border)',
                  boxShadow: 'none',
                  backgroundColor: state.isDisabled
                    ? 'var(--color-background-dark)'
                    : 'var(--color-input)',
                  color: 'var(--color-foreground)',
                  placeholderColor: 'var(--color-foreground)',
                  ':hover': {
                    borderColor: 'var(--color-border)',
                  },
                }),
                multiValue: (base) => ({
                  ...base,
                  backgroundColor: 'var(--color-gray-light)',
                  color: 'var(--color-foreground)',
                }),
                multiValueLabel: (base) => ({
                  ...base,
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: 'var(--color-foreground)',
                }),
                multiValueRemove: (base) => ({
                  ...base,
                  ':hover': {
                    backgroundColor: 'var(--color-destructive)',
                    color: 'var(--color-white)',
                  },
                }),
                dropdownIndicator: (base) => ({
                  ...base,
                  padding: '8px',
                }),
                valueContainer: (base) => ({
                  ...base,
                  padding: '4px 8px',
                }),
                menu: (base) => ({
                  ...base,
                  fontSize: '0.875rem',
                  backgroundColor: 'var(--color-background)',
                }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isSelected
                    ? 'var(--color-background-light)'
                    : state.isFocused
                      ? 'var(--color-background-dark)'
                      : 'var(--color-background-light)',
                  color: 'var(--color-foreground)',
                  ':active': {
                    backgroundColor: 'var(--color-background-dark)',
                  },
                }),
              }}
            />
          )}
        </div>

        {/* Date Filters */}
        <div className="flex flex-row flex-wrap items-end gap-4 pt-4 border-t border-border">
          <div className="flex flex-col gap-1">
            <Label htmlFor="sunburst-start-date" className="text-sm font-medium">
              {intl.formatMessage({ id: 'detections.filter.fromDate' })}
            </Label>
            <input
              id="sunburst-start-date"
              type="datetime-local"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              onClick={(e) => e.currentTarget.showPicker?.()}
              className="px-3 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="sunburst-end-date" className="text-sm font-medium">
              {intl.formatMessage({ id: 'detections.filter.toDate' })}
            </Label>
            <input
              id="sunburst-end-date"
              type="datetime-local"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              onClick={(e) => e.currentTarget.showPicker?.()}
              className="px-3 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </div>
          <div className="flex gap-2">
            <Button variant="default" size="sm" onClick={handleApplyFilters}>
              {intl.formatMessage({ id: 'common.apply' })}
            </Button>
            {(appliedFilters.from || appliedFilters.to) && (
              <Button variant="outline" size="sm" onClick={handleClearFilters}>
                {intl.formatMessage({ id: 'common.reset' })}
              </Button>
            )}
          </div>
        </div>

        {/* Status Filters */}
        <div className="pt-4 border-t border-border">
          <div className="flex items-center justify-between mb-3">
            <Label className="text-sm font-medium">
              {intl.formatMessage({ id: 'detectionsSunburst.statusFilter.title' })}
            </Label>
            <span className="text-xs text-foreground">
              {intl.formatMessage(
                { id: 'detectionsSunburst.statusFilter.showing' },
                {
                  filtered: Array.from(selectedStatuses).reduce(
                    (sum, detectionStatus) => sum + statusCounts[detectionStatus],
                    0,
                  ),
                  total: totalFromCounts,
                },
              )}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {ALL_STATUSES.map((status) => {
              const isSelected = selectedStatuses.has(status);
              const count = statusCounts[status];
              return (
                <button
                  key={status}
                  type="button"
                  onClick={() => toggleStatus(status)}
                  data-selected={isSelected}
                  className={`
                    flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium
                    transition-all duration-150 border
                    ${
                      isSelected
                        ? `${STATUS_BG_COLORS[status]} ${STATUS_TEXT_COLORS[status]}`
                        : 'bg-disable-light border-disable text-disable-dark'
                    }
                    hover:opacity-80 cursor-pointer
                  `}
                >
                  <span
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: isSelected ? STATUS_COLORS[status] : '#9ca3af' }}
                  />
                  <span>{intl.formatMessage({ id: getStatusTranslationKey(status) })}</span>
                  <span className="font-bold">({count})</span>
                </button>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
